import { isPlatformBrowser } from '@angular/common'
import { Injectable, PLATFORM_ID, inject } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { CookieService } from 'ngx-cookie-service'
import { filter } from 'rxjs/operators'
import { HttpClient } from '@angular/common/http'
import { firstValueFrom } from 'rxjs'

export const AFFILIATES_COOKIE = 'affiliate_cookie'
export const SESSION_COOKIE = 'session'

const INITIAL_TRAFFIC_SOURCE = 'initialTrafficSource'
const CID = 'cid'

interface NavigationPayload {
  id: number
  url: string
  urlAfterRedirects: string
}

@Injectable({
  providedIn: 'root',
})
export class CustomerNavigationService {
  private http = inject(HttpClient)
  private router = inject(Router)
  private cookieService = inject(CookieService)
  private platformId = inject(PLATFORM_ID)
  private readonly isBrowser: boolean

  private session = ''

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId)
    if (!this.isBrowser) {
      return
    }

    this.session = this.cookieService.get(SESSION_COOKIE)
    if (!this.session) {
      this.setSession(this.generateSession())
    }

    const affiliateData = this.cookieService.get(AFFILIATES_COOKIE)
    const cid = this.cookieService.get(CID)
    const source = this.parseSourceCookie()

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(async (event: NavigationEnd) => {
      try {
        const url = event.urlAfterRedirects
        // We have to skip track of dashboard navigation
        if (url.startsWith('/error')) {
          return
        }

        // If it is the first page of the application, we have to init customer data track
        // We have to re-init session once customer authenticated
        if (url.startsWith('/loans/') || url.startsWith('/customer-details')) {
          await this.trackCustomerData(cid, this.session, affiliateData, { source })
        }

        await this.trackCustomerNavigation(this.session, event)
      } catch (error) {
        console.error(error)
      }
    })
  }

  init() {
    /* empty */
  }

  parseSourceCookie() {
    const sourceCookie = this.cookieService.get(INITIAL_TRAFFIC_SOURCE)
    const source: any = {}
    if (sourceCookie) {
      sourceCookie.split('|').forEach(record => {
        const [utmKey, utmValue] = record.split('=')
        source[utmKey] = utmValue
      })
    }
    return source
  }

  generateSession(): string {
    return [...Array(32)].map(() => (Math.random() + new Date().getTime()).toString(36).slice(-1)).join('')
  }

  setSession(session: string) {
    this.session = session
    this.cookieService.set(SESSION_COOKIE, this.session)
  }

  trackCustomerData(cid: string, session: string, affiliateData: string, data: any) {
    if (!this.isBrowser) {
      return Promise.resolve()
    }

    data.cid = cid
    data.session = session
    data.affiliate_data = affiliateData
    return firstValueFrom(this.http.post('/api/public/v1/track/data', { data }), { defaultValue: undefined })
  }

  trackCustomerNavigation(session: string, event: NavigationEnd) {
    if (!this.isBrowser) {
      return Promise.resolve()
    }

    const navigation: NavigationPayload = {
      id: event.id,
      url: event.url,
      urlAfterRedirects: event.urlAfterRedirects,
    }

    return firstValueFrom(this.http.post('/api/public/v1/track/navigation', { data: { session, event: navigation } }), {
      defaultValue: undefined,
    })
  }
}
